import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Autocomplete, Badge, Box, ButtonBase, CircularProgress, Drawer, IconButton, InputBase, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { keyframes } from "@mui/system";
import {
  AddComment as NewChatIcon,
  ArrowUpward as SendIcon,
  AssignmentOutlined as HomeworkIcon,
  Close as CloseIcon,
  CloseFullscreen as CollapseIcon,
  EventOutlined as EventIcon,
  HelpOutlineOutlined as HelpIcon,
  HomeOutlined as HomeIcon,
  InsightsOutlined as ProgressIcon,
  OpenInFull as ExpandIcon,
  SchoolOutlined as SchoolIcon,
  WarningAmberOutlined as AttentionIcon,
  TrendingDownOutlined as WeakIcon,
  Groups2Outlined as GroupsIcon,
  RateReviewOutlined as CommentIcon,
  SupportAgentOutlined as SupportAgentIcon,
  TipsAndUpdatesOutlined as TipIcon,
  VideocamOutlined as JoinIcon,
  DescriptionOutlined as ReportIcon,
  PictureAsPdfOutlined as PdfIcon,
  TableChartOutlined as CsvIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import api from "../../api";
import AssistantLogo from "./AssistantLogo";

/* 11 Plus Magic palette — matches the portal sidebar (indigo → red), not a stock purple. */
const C = {
  ink: "#1F1A4D",
  indigo: "#5A4FCF",
  /* the portal top bar's own gradient, so launcher and header read as part of the portal */
  brand: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  indigoSoft: "#EDEBFA",
  red: "#D62926",
  wash: "#F6F5FC",
  line: "#E3E0F2",
  muted: "#6B6790",
  live: "#0E9F6E",
  gold: "#F5A700",
};

const TILES = {
  // course: only for students on a course (the sidebar's courseOnly). noCourse: their replacement
  // for mobile-plan-only students. locked: shown when the plan has ended.
  student: [
    { label: "How am I doing?", icon: ProgressIcon },
    { label: "When is my next class?", icon: EventIcon, course: true },
    { label: "What homework do I have left?", icon: HomeworkIcon, course: true },
    { label: "What should I practise today?", icon: TipIcon, noCourse: true },
    { label: "What does my plan include?", icon: HomeworkIcon, noCourse: true, locked: true },
    { label: "Help me with a question", icon: HelpIcon },
  ],
  parent: [
    { label: "Full status: how much has been done?", short: "Full progress status", icon: ProgressIcon },
    { label: "When are the next classes?", short: "Next classes", icon: EventIcon, course: true },
    { label: "What homework is still pending?", short: "Pending homework", icon: HomeworkIcon, course: true },
    { label: "What should my child practise this week?", short: "What to practise", icon: HomeworkIcon, noCourse: true },
    { label: "How can I help at home?", short: "How to help at home", icon: TipIcon },
  ],
  tutor: [
    { label: "How are my groups doing?", short: "How are my groups doing?", icon: GroupsIcon },
    { label: "Who is behind on homework?", short: "Who is behind on homework?", icon: HomeworkIcon },
    { label: "When is my next class?", short: "My next classes", icon: EventIcon },
    { label: "Draft a short progress comment for parents.", short: "Draft a progress comment", icon: CommentIcon },
  ],
};

TILES.admin = [
  { label: "What needs attention today?", short: "What needs attention today?", icon: AttentionIcon },
  { label: "Which subjects and topics are missing worksheets?", short: "Missing worksheets", icon: HomeworkIcon },
  { label: "Which schools are missing practice papers or mock exams?", short: "Schools missing papers", icon: SchoolIcon },
  { label: "What are the weakest topics across students, and do they have worksheets?", short: "Weakest topics", icon: WeakIcon },
];

/** One-click reports per role (no model call; POST {role}/assistant/reports). */
function reportButtons(role, { childId, focusStudent, staffKind }) {
  if (role === "parent") {
    return childId
      ? [
          { label: "Progress report", type: "student_progress", params: { student_id: childId } },
          { label: "Weak areas report", type: "student_weak_areas", params: { student_id: childId } },
        ]
      : [];
  }
  if (role === "tutor") {
    const first = focusStudent?.full_name?.split(" ")[0];
    return [
      ...(focusStudent
        ? [
            { label: `Progress report: ${first}`, type: "student_progress", params: { student_id: focusStudent.id } },
            { label: `Weak areas: ${first}`, type: "student_weak_areas", params: { student_id: focusStudent.id } },
          ]
        : []),
      { label: "Group report", type: "group", params: {} },
    ];
  }
  if (role === "admin") {
    return [
      { label: "Schools missing papers", type: "school_paper_gaps", params: {} },
      { label: "Worksheet gaps", type: "worksheet_gaps", params: {} },
      { label: "Weakest topics", type: "weak_topics", params: {} },
      { label: "Operations", type: "operations", params: {} },
      ...(staffKind === "superadmin" ? [{ label: "Business figures", type: "business", params: { period: "this_month" } }] : []),
    ];
  }
  return [];
}

/**
 * "More questions" under the tiles: each chip sends its full sentence. A `needsFocus` question
 * is about the tutor's focus student, so it is not sent until one is picked.
 * School-gap, mock exam and revenue questions exist only in the staff lists.
 */
const ONE_STUDENT = (text) => ({ text, needsFocus: true });

const MORE_QUESTIONS = {
  student: [
    {
      group: "My progress",
      items: ["What should I practise next?", "Which topics am I best at?", "What are my weakest topics?", "Make me a short plan for this week."],
    },
    {
      group: "Practice",
      items: [
        "Show me a worksheet for my weakest topic.",
        "Where are the flash cards for my weak topic?",
        "Where do I find mock exams?",
        "Where do I find practice papers?",
      ],
    },
    {
      group: "Classes and homework",
      items: ["Is any homework overdue?", "What homework is due this week?", "Where do I join my class?", "Where are the class recordings?"],
    },
    {
      group: "Find a page",
      items: ["Where is the Wizard?", "Where are my certificates?", "Where are my messages?", "Where is the games leaderboard?"],
    },
  ],
  parent: [
    {
      group: "This child",
      items: [
        "What are the weakest topics, and what should we practise?",
        "Which topics are strongest?",
        "How ready is my child for the target school?",
        "What did my child finish in the last 30 days?",
      ],
    },
    {
      group: "Reports and results",
      items: [
        "Is any homework overdue?",
        "Give me a progress report.",
        "Give me a weak areas report.",
        "Where are the mock exam results?",
        "Where are the worksheet results?",
        "Where are the practice paper results?",
      ],
    },
    {
      group: "Classes and the study coach",
      items: ["When is the next class?", "Where can I read what my child asked the study coach?", "Where is the end of term report?"],
    },
    {
      group: "Account",
      items: ["How do I add another child?", "How do I change my child's password?", "Where is billing?", "Where are my purchases?"],
    },
  ],
  tutor: [
    {
      group: "My groups",
      items: [
        "Who should I chase before the next class?",
        "Who has overdue homework?",
        "Which students have not been active recently?",
        "Write a group report.",
      ],
    },
    {
      group: "One student",
      items: [
        ONE_STUDENT("How is this student doing?"),
        ONE_STUDENT("What are this student's weakest topics?"),
        ONE_STUDENT("What homework does this student still have?"),
        ONE_STUDENT("Give me a progress report for this student."),
        ONE_STUDENT("Give me a weak areas report for this student."),
      ],
    },
    {
      group: "Teaching",
      items: [
        "Give me a 5-minute starter for our weakest topic.",
        "Give me a worked example and three practice questions for that topic.",
        "What did parents rate my recent classes?",
      ],
    },
    {
      group: "Find a page",
      items: [
        "When is my next class, and where do I join?",
        "Where are class recordings?",
        "Where do I see a student's term report?",
        "Where are my groups and their weeks?",
        "Where are awards and certificates?",
      ],
    },
  ],
};

const STAFF_MORE = [
  "Who is falling behind?",
  "Which group weeks have nothing allocated?",
  "Which questions are missing videos or explanations?",
  "Which schools that students target have no practice papers?",
  "Which schools are missing Maths papers?",
  "Which schools have practice papers but no mock exam?",
  "Which papers should we turn into a mock exam for the schools students want most?",
];

const STAFF_EXTRA = {
  admin: ["How do I look up one student's progress?"],
  tester: ["Where do I review flagged questions?"],
  superadmin: [],
};

function moreQuestions(role, staffKind) {
  if (role !== "admin") return MORE_QUESTIONS[role] || [];
  const kind = STAFF_EXTRA[staffKind] ? staffKind : "admin";
  return [
    { group: "Staff questions", items: [...STAFF_MORE, ...STAFF_EXTRA[kind]] },
    ...(kind === "superadmin"
      ? [
          {
            group: "Business (Super Admin)",
            items: [
              "What was the revenue this month?",
              "What are refunds and active subscriptions this month?",
              "Give me a business report for this year.",
            ],
          },
        ]
      : []),
  ];
}

const ROLE_COPY = {
  student: {
    title: "Magic",
    subtitle: "Your AI study coach",
    launcher: "Ask Magic",
    aria: "Open Magic, your AI study coach",
    footer: "Magic is an AI and can make mistakes.",
  },
  parent: {
    title: "Progress Assistant",
    subtitle: "AI help for parents",
    launcher: "Ask about progress",
    aria: "Open the progress assistant",
    footer: "This assistant is an AI and can make mistakes.",
  },
  admin: {
    title: "Admin Assistant",
    subtitle: "AI help across the admin panel",
    launcher: "Admin assistant",
    aria: "Open the AI admin assistant",
    footer: "This assistant is an AI and can make mistakes. Check figures before acting on them.",
  },
  tutor: {
    title: "Teaching Assistant",
    subtitle: "AI help with your groups and students",
    launcher: "Teaching assistant",
    aria: "Open your AI teaching assistant",
    footer: "This assistant is an AI and can make mistakes.",
  },
};

const HUMAN_STATUSES = ["queued_for_human", "assigned"];

// No AI help while a test, worksheet, mock exam or paper is being taken, or in a live class.
const TEST_IN_PROGRESS = /\/(take|test)(\/|$)|\/(topic|subtopic)\/test\/|\/meeting\//;
// Admin pages that are focused review workspaces (the panel would cover the work).
const ADMIN_FOCUSED = /^\/admin\/(question-bank\/import\/[^/]+\/review|question-bank\/mcq-review|question-bank\/papers\/[^/]+|mock-exam\/result\/|mobile-users\/[^/]+\/worksheet\/)/;

const REMINDER_POLL_MS = 60000;
const DISMISSED_KEY = "portal-assistant-dismissed-classes";

const bounce = keyframes`
  0%, 100% { transform: translateY(0) rotate(0); }
  30% { transform: translateY(-7px) rotate(-8deg); }
  60% { transform: translateY(0) rotate(6deg); }
`;
const dot = keyframes`
  0%, 80%, 100% { opacity: .25; transform: translateY(0); }
  40% { opacity: 1; transform: translateY(-3px); }
`;
// Shared by report chips and "More questions" chips.
const chipSx = {
  px: 1.25,
  py: 0.75,
  gap: 0.75,
  borderRadius: "999px",
  border: `1px solid ${C.line}`,
  bgcolor: "#fff",
  fontSize: 13,
  fontWeight: 600,
  color: C.ink,
  textAlign: "left",
  "&:hover": { borderColor: C.indigo, bgcolor: C.indigoSoft },
  "&:focus-visible": { outline: `2px solid ${C.indigo}`, outlineOffset: 2 },
  "&.Mui-disabled": { opacity: 0.6 },
};

const reduceMotion = { "@media (prefers-reduced-motion: reduce)": { animation: "none" } };

function readDismissed() {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]");
  } catch {
    return [];
  }
}

function whenLabel(r) {
  if (r.status === "ongoing") return "is on now";
  if (r.minutes_until <= 1) return "starts now";
  if (r.minutes_until < 60) return `starts in ${r.minutes_until} min`;
  return `starts at ${new Date(r.start_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
}

function mergeMessages(existing, incoming) {
  const map = new Map(existing.map((m) => [m.id, m]));
  incoming.forEach((m) => map.set(m.id, m));
  return Array.from(map.values()).sort((a, b) => a.id - b.id);
}

/** Inline **bold** and [label](/path | https://…) — in-app paths navigate without a reload. */
function renderInline(text, onInternalLink, keyPrefix) {
  const value = String(text || "");
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[a-zA-Z0-9\-_/]*(?:\?[^\s)]+)?)\)|\*\*([^*]+)\*\*/g;
  const parts = [];
  let cursor = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(value)) !== null) {
    if (match.index > cursor) parts.push(<React.Fragment key={`${keyPrefix}-${key++}`}>{value.slice(cursor, match.index)}</React.Fragment>);
    if (match[3] !== undefined) {
      parts.push(<strong key={`${keyPrefix}-${key++}`}>{match[3]}</strong>);
    } else {
      const [, label, href] = match;
      const external = href.startsWith("http");
      parts.push(
        <Box
          component="a"
          key={`${keyPrefix}-${key++}`}
          href={href}
          target={external ? "_blank" : undefined}
          rel="noreferrer"
          onClick={(e) => {
            if (!external) {
              e.preventDefault();
              onInternalLink(href);
            }
          }}
          sx={{
            color: "inherit",
            fontWeight: 600,
            textDecoration: "underline",
            textDecorationColor: "currentColor",
            textUnderlineOffset: "3px",
            "&:hover": { textDecorationThickness: "2px" },
          }}
        >
          {label}
        </Box>
      );
    }
    cursor = regex.lastIndex;
  }
  if (cursor < value.length) parts.push(<React.Fragment key={`${keyPrefix}-${key++}`}>{value.slice(cursor)}</React.Fragment>);

  return parts;
}

/** Paragraphs, "- " bullets and "1. " steps — the shapes the coach actually answers in. */
function renderRich(text, onInternalLink) {
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) blocks.push(list);
    list = null;
  };

  String(text || "")
    .split("\n")
    .forEach((raw, i) => {
      const line = raw.trimEnd();
      const bullet = line.match(/^\s*[-•*]\s+(.*)$/);
      const step = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
      if (bullet || step) {
        const type = bullet ? "ul" : "ol";
        if (!list || list.type !== type) {
          flush();
          list = { type, items: [] };
        }
        list.items.push(bullet ? bullet[1] : step[2]);
        return;
      }
      flush();
      if (line.trim() !== "") blocks.push({ type: "p", text: line, i });
    });
  flush();

  return blocks.map((b, i) =>
    b.type === "p" ? (
      <Box component="p" key={i} sx={{ m: 0, "& + *": { mt: 1 } }}>
        {renderInline(b.text, onInternalLink, `p${i}`)}
      </Box>
    ) : (
      <Box component={b.type} key={i} sx={{ m: 0, pl: 2.5, "& + *": { mt: 1 }, "& li + li": { mt: 0.5 }, "& li::marker": { color: C.indigo, fontWeight: 700 } }}>
        {b.items.map((item, j) => (
          <li key={j}>{renderInline(item, onInternalLink, `l${i}-${j}`)}</li>
        ))}
      </Box>
    )
  );
}

function ClassCard({ r, onJoin, onView }) {
  const live = r.status === "ongoing";
  const canJoin = r.join_url && (live || r.minutes_until <= 10);
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: 1.5,
        borderRadius: "14px",
        bgcolor: "#fff",
        border: `1px solid ${C.line}`,
        boxShadow: `inset 4px 0 0 ${live ? C.live : C.gold}`,
      }}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "12px", display: "grid", placeItems: "center", bgcolor: live ? "#E3F7EF" : "#FFF4D6", color: live ? C.live : "#B07800" }}>
        <EventIcon fontSize="small" />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink }} noWrap>
          {r.child_name ? `${r.child_name}'s class ` : "Your class "}
          {whenLabel(r)}
        </Typography>
        <Typography sx={{ fontSize: 12.5, color: C.muted }} noWrap>
          {r.name}
          {r.group ? `, ${r.group}` : ""}
        </Typography>
      </Box>
      {canJoin ? (
        <ButtonBase
          onClick={onJoin}
          sx={{ px: 1.5, py: 0.75, borderRadius: "999px", bgcolor: C.live, color: "#fff", fontSize: 13, fontWeight: 700, gap: 0.5 }}
        >
          <JoinIcon sx={{ fontSize: 18 }} /> Join
        </ButtonBase>
      ) : (
        <ButtonBase onClick={onView} sx={{ px: 1.5, py: 0.75, borderRadius: "999px", color: C.indigo, fontSize: 13, fontWeight: 700, border: `1px solid ${C.line}` }}>
          View
        </ButtonBase>
      )}
    </Box>
  );
}

/**
 * Floating AI assistant for the student and parent portals.
 * The 11 Plus Magic emblem is the assistant's face (AssistantLogo).
 * role="student": "Magic", the study coach, on the student's own progress and classes.
 * role="parent": progress assistant about one chosen child (defaults to activeStudentId).
 */
export default function AssistantDrawer({ role = "student", activeStudentId = null, staffKind = "admin" }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const base = `${role}/assistant`;
  const isParent = role === "parent";
  const isTutor = role === "tutor";
  const isAdmin = role === "admin";
  const copy = ROLE_COPY[role] || ROLE_COPY.student;
  // Parent: the chosen child (required). Tutor: an optional focus student ("" = all my students).
  const sameSelection = (conv, id) =>
    isTutor ? String(conv?.student?.id ?? "") === String(id || "") : !isParent || Number(conv?.student?.id) === Number(id);

  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [enabled, setEnabled] = useState(true);
  // What this user / child can open (server: PortalAccessProfile) — drives which tiles show.
  const [access, setAccess] = useState(null);
  const [remaining, setRemaining] = useState(null);
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState(activeStudentId || "");
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [reminders, setReminders] = useState([]);
  const [dismissed, setDismissed] = useState(readDismissed);
  const [focusHint, setFocusHint] = useState(false);
  // "More questions" chips stay folded until asked for.
  const [showMore, setShowMore] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const hidden =
    TEST_IN_PROGRESS.test(pathname) ||
    (isAdmin && ADMIN_FOCUSED.test(pathname)) ||
    // Resource Allocation's phone-width buttons sit in the same corner as the launcher.
    (isAdmin && isMobile && pathname.startsWith("/admin/resource-allocation"));

  const lastId = messages.length ? messages[messages.length - 1].id : 0;
  const humanMode = conversation && HUMAN_STATUSES.includes(conversation.status);
  const closed = conversation?.status === "closed";
  const asked = messages.some((m) => m.sender_type === "visitor");

  useEffect(() => {
    if (activeStudentId) setChildId(activeStudentId);
  }, [activeStudentId]);

  useEffect(() => {
    setShowMore(false);
    setFocusHint(false);
  }, [conversation?.id, childId]);

  // Class reminders drive the badge/bubble on every panel page, even with the drawer closed.
  useEffect(() => {
    // Class reminders are for students, parents and tutors; staff have no classes of their own.
    if (hidden || isAdmin) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`${base}/reminders`);
        if (!cancelled) setReminders(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch {
        /* reminders are best-effort */
      }
    };
    load();
    const t = setInterval(load, REMINDER_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [base, hidden]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const startConversation = useCallback(
    async (forChildId) => {
      setLoading(true);
      setError("");
      try {
        const body = isParent || (isTutor && forChildId) ? { student_id: forChildId } : {};
        const res = await api.post(`${base}/conversations`, body);
        const conv = res.data?.data;
        setConversation(conv);
        setMessages(conv?.messages || []);
      } catch (e) {
        setError(e.response?.data?.message || "Could not start the assistant. Close it and try again.");
      } finally {
        setLoading(false);
      }
    },
    [base, isParent, isTutor]
  );

  /** Resume the latest open chat (for this child) or start a fresh one. */
  const loadOrStart = useCallback(
    async (forChildId) => {
      if (isParent && !forChildId) return;
      setLoading(true);
      setError("");
      try {
        const list = await api.get(`${base}/conversations`);
        const rows = Array.isArray(list.data?.data) ? list.data.data : [];
        const resumable = rows.find((c) => c.status !== "closed" && sameSelection(c, forChildId));
        if (resumable) {
          const res = await api.get(`${base}/conversations/${resumable.id}/messages`);
          setConversation(res.data?.data?.conversation);
          setMessages(res.data?.data?.messages || []);
          setLoading(false);
          return;
        }
      } catch (e) {
        // fall through to a fresh chat
      }
      await startConversation(forChildId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [base, isParent, isTutor, startConversation]
  );

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${base}/status`, { params: isParent && childId ? { student_id: childId } : {} });
        if (cancelled) return;
        setEnabled(res.data?.data?.enabled !== false);
        setRemaining(res.data?.data?.remaining_today ?? null);
        setAccess(res.data?.data?.access ?? null);
      } catch {
        /* status is advisory */
      }
      if (isParent && children.length === 0) {
        try {
          const res = await api.get("parent/students/names");
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          if (cancelled) return;
          setChildren(list);
          if (!childId && list.length) setChildId(list[0].id);
        } catch {
          /* handled by empty state */
        }
      }
      if (isTutor && children.length === 0) {
        try {
          const res = await api.get("tutor/assistant/students");
          const list = Array.isArray(res.data?.data) ? res.data.data : [];
          if (!cancelled) setChildren(list.map((c) => ({ ...c, full_name: c.name })));
        } catch {
          /* the picker just stays on "All my students" */
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, childId]);

  useEffect(() => {
    if (!open || !enabled) return;
    if (isParent && !childId) return;
    if (conversation && sameSelection(conversation, childId)) return;
    loadOrStart(childId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, enabled, childId]);

  // Poll only while a team member is involved — bot replies come back on the POST.
  useEffect(() => {
    if (!open || !conversation || !humanMode) return undefined;
    const t = setInterval(async () => {
      try {
        const res = await api.get(`${base}/conversations/${conversation.id}/messages`, { params: { after_id: lastId } });
        setConversation(res.data?.data?.conversation);
        setMessages((prev) => mergeMessages(prev, res.data?.data?.messages || []));
      } catch {
        /* keep polling */
      }
    }, 6000);
    return () => clearInterval(t);
  }, [open, conversation, humanMode, base, lastId]);

  const post = async (payload, optimisticText) => {
    if (!conversation || sending) return;
    setSending(true);
    setError("");
    if (optimisticText) {
      setMessages((prev) => [...prev, { id: `tmp-${Date.now()}`, sender_type: "visitor", body: optimisticText }]);
    }
    try {
      const res = await api.post(`${base}/conversations/${conversation.id}/messages`, payload);
      const data = res.data?.data || {};
      setConversation(data.conversation);
      setMessages((prev) => mergeMessages(prev.filter((m) => !String(m.id).startsWith("tmp-")), data.messages || []));
      if (data.remaining_today !== undefined) setRemaining(data.remaining_today);
    } catch (e) {
      setMessages((prev) => prev.filter((m) => !String(m.id).startsWith("tmp-")));
      setError(e.response?.data?.message || "Your message was not sent. Check your connection and send it again.");
      if (optimisticText) setInput(optimisticText);
    } finally {
      setSending(false);
    }
  };

  const [reporting, setReporting] = useState("");
  const createReport = async (btn) => {
    if (!conversation || reporting) return;
    setReporting(btn.type);
    setError("");
    try {
      const res = await api.post(`${base}/reports`, { type: btn.type, ...btn.params, conversation_id: conversation.id });
      setMessages((prev) => mergeMessages(prev, res.data?.data?.messages || []));
    } catch (e) {
      setError(e.response?.data?.message || "The report could not be created. Try again, or ask for it in the chat.");
    } finally {
      setReporting("");
    }
  };

  const send = (text) => {
    const message = String(text ?? input).trim();
    if (!message) return;
    setInput("");
    post({ message }, message);
  };

  const talkToTeam = () => post(conversation?.awaiting_handoff ? { confirm_handoff: true } : { request_human: true, message: "" });

  const onInternalLink = (href) => {
    navigate(href);
    if (isMobile) setOpen(false);
  };

  const joinClass = (r) => window.open(`${window.location.origin}${r.join_url}`, "_blank", "noopener");
  const viewClasses = () => {
    navigate(`/${role}/classes`);
    if (isMobile) setOpen(false);
  };

  if (hidden) return null;

  const activeReminders = reminders.filter((r) => !dismissed.includes(r.class_id));
  const bubble = activeReminders[0];
  const dismissBubble = () => {
    const next = [...dismissed, bubble.class_id];
    setDismissed(next);
    try {
      sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(next));
    } catch {
      /* private mode: dismissal lasts until reload */
    }
  };

  const childName = children.find((c) => Number(c.id) === Number(childId))?.full_name?.split(" ")[0];
  const greeting = messages.find((m) => m.sender_type === "bot");
  const title = copy.title;
  const subtitle = humanMode
    ? conversation?.status === "assigned"
      ? "A team member is chatting with you"
      : "Waiting for a team member"
    : copy.subtitle;
  const focusStudent = isTutor ? children.find((c) => Number(c.id) === Number(childId)) || null : null;
  const focusFirst = focusStudent?.full_name?.split(" ")[0];
  const noCourse = !!access?.hide_course_only;
  const tiles = (TILES[role] || [])
    .filter((t) => (t.course ? !noCourse : true) && (t.noCourse ? noCourse : true))
    .filter((t) => (access?.plan_fully_locked ? !t.course && (t.locked || !t.noCourse) : !t.locked || noCourse))
    .slice(0, 4)
    .map((t) =>
    isTutor && focusFirst && t.icon === CommentIcon
      ? { ...t, label: `Draft a short progress comment for ${focusFirst}.`, short: `Draft a short progress comment for ${focusFirst}.` }
      : t
  );
  const reports = reportButtons(role, { childId, focusStudent: isTutor ? focusStudent : null, staffKind });
  const askChip = (item) => {
    if (item.needsFocus && !focusStudent) {
      setFocusHint(true);
      return;
    }
    setFocusHint(false);
    send(item.text);
  };

  return (
    <>
      {/* Class reminder bubble above the launcher */}
      {!open && bubble && (
        <Box
          role="status"
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 88, sm: 100 },
            zIndex: (t) => t.zIndex.drawer + 2,
            width: { xs: "calc(100% - 32px)", sm: 340 },
            filter: "drop-shadow(0 10px 24px rgba(31,26,77,.18))",
          }}
        >
          <ClassCard r={bubble} onJoin={() => joinClass(bubble)} onView={viewClasses} />
          <IconButton
            size="small"
            onClick={dismissBubble}
            aria-label="Dismiss class reminder"
            sx={{ position: "absolute", top: -10, right: -10, bgcolor: "#fff", border: `1px solid ${C.line}`, width: 26, height: 26, "&:hover": { bgcolor: C.wash } }}
          >
            <CloseIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>
      )}

      {/* Launcher: a compact round button so it never hides page content; the label slides out on hover/focus */}
      {!open && (
        <ButtonBase
          onClick={() => setOpen(true)}
          aria-label={copy.aria}
          sx={{
            position: "fixed",
            right: { xs: 16, sm: 24 },
            bottom: { xs: 16, sm: 24 },
            zIndex: (t) => t.zIndex.drawer + 2,
            height: 60,
            minWidth: 60,
            px: "8px",
            borderRadius: "999px",
            background: C.brand,
            color: "#fff",
            boxShadow: "0 10px 28px rgba(102,94,220,.40)",
            transition: "transform .15s ease, box-shadow .15s ease",
            "& .assistant-label": { maxWidth: 0, opacity: 0, ml: 0, transition: "max-width .2s ease, opacity .2s ease, margin .2s ease" },
            "&:hover, &:focus-visible": {
              transform: "translateY(-2px)",
              boxShadow: "0 14px 32px rgba(102,94,220,.48)",
              "& .assistant-label": { maxWidth: 180, opacity: 1, ml: 1, mr: 1 },
            },
            "&:focus-visible": { outline: `3px solid ${C.gold}`, outlineOffset: 3 },
            "@media (prefers-reduced-motion: reduce)": { transition: "none", "& .assistant-label": { transition: "none" } },
          }}
        >
          <Badge color="error" variant="dot" invisible={!reminders.length} overlap="circular" sx={{ "& .MuiBadge-dot": { width: 12, height: 12, borderRadius: 6, border: "2px solid #fff" } }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "50%", bgcolor: "#fff", display: "grid", placeItems: "center", animation: reminders.length ? `${bounce} 1s ease 2` : "none", ...reduceMotion }}>
              <AssistantLogo size={34} />
            </Box>
          </Badge>
          <Box component="span" className="assistant-label" sx={{ overflow: "hidden", whiteSpace: "nowrap", fontWeight: 700, fontSize: 15 }}>
            {copy.launcher}
          </Box>
        </ButtonBase>
      )}

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: wide ? 680 : 440 },
            transition: "width .2s ease",
            display: "flex",
            flexDirection: "column",
            bgcolor: C.wash,
            borderTopLeftRadius: { sm: 20 },
            borderBottomLeftRadius: { sm: 20 },
            overflow: "hidden",
          },
        }}
        sx={{ zIndex: (t) => t.zIndex.drawer + 3 }}
      >
        {/* Header */}
        <Box sx={{ position: "relative", px: 2, pt: 1.75, pb: (isParent && children.length > 1) || (isTutor && children.length > 0) ? 1.25 : 1.75, color: "#fff", background: C.brand, overflow: "hidden" }}>
          {/* faint star field, the one decorative touch */}
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              opacity: 0.16,
              backgroundImage:
                "radial-gradient(circle at 18% 30%, #fff 1.2px, transparent 1.6px), radial-gradient(circle at 72% 18%, #fff 1px, transparent 1.4px), radial-gradient(circle at 88% 70%, #FFD54A 1.6px, transparent 2px), radial-gradient(circle at 45% 80%, #fff 1px, transparent 1.4px)",
              backgroundSize: "120px 70px",
            }}
          />
          <Box sx={{ position: "relative", display: "flex", alignItems: "center", gap: 1.25 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "14px", bgcolor: humanMode ? "rgba(255,255,255,.14)" : "#fff", display: "grid", placeItems: "center" }}>
              {humanMode ? <SupportAgentIcon /> : <AssistantLogo size={36} />}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontWeight: 800, fontSize: 17, lineHeight: 1.2 }} noWrap>
                {humanMode ? "Our team" : title}
              </Typography>
              <Typography sx={{ fontSize: 12.5, opacity: 0.85, display: "flex", alignItems: "center", gap: 0.75 }} noWrap>
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: humanMode ? C.gold : "#5EE0A8" }} />
                {subtitle}
              </Typography>
            </Box>
            {asked && (
              <Tooltip title="Back to start">
                <span>
                  <IconButton color="inherit" size="small" disabled={loading || sending || (isParent && !childId)} onClick={() => startConversation(childId)} aria-label="Start a new chat">
                    <HomeIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {!isMobile && (
              <Tooltip title={wide ? "Make smaller" : "Make wider"}>
                <IconButton color="inherit" size="small" onClick={() => setWide((w) => !w)} aria-label={wide ? "Make the assistant narrower" : "Make the assistant wider"}>
                  {wide ? <CollapseIcon fontSize="small" /> : <ExpandIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}
            <IconButton color="inherit" size="small" onClick={() => setOpen(false)} aria-label="Close assistant">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Parent: switch child */}
          {isParent && children.length > 1 && (
            <Box sx={{ position: "relative", display: "flex", gap: 0.75, mt: 1.25, overflowX: "auto", pb: 0.25 }}>
              {children.map((c) => {
                const active = Number(c.id) === Number(childId);
                return (
                  <ButtonBase
                    key={c.id}
                    onClick={() => {
                      if (active) return;
                      setConversation(null);
                      setMessages([]);
                      setChildId(c.id);
                    }}
                    aria-pressed={active}
                    sx={{
                      flexShrink: 0,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "999px",
                      fontSize: 13,
                      fontWeight: 700,
                      color: active ? C.indigo : "#fff",
                      bgcolor: active ? "#fff" : "rgba(255,255,255,.14)",
                    }}
                  >
                    {c.full_name}
                  </ButtonBase>
                );
              })}
            </Box>
          )}

          {/* Tutor: optional focus student — searchable, tutors can have many students */}
          {isTutor && children.length > 0 && (
            <Autocomplete
              size="small"
              options={children}
              value={focusStudent}
              onChange={(e, picked) => {
                const next = picked ? picked.id : "";
                if (String(next) === String(childId || "")) return;
                setConversation(null);
                setMessages([]);
                setChildId(next);
              }}
              getOptionLabel={(o) => o.full_name || ""}
              isOptionEqualToValue={(o, v) => Number(o.id) === Number(v.id)}
              renderOption={(props, o) => (
                <li {...props} key={o.id}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{o.full_name}</Typography>
                    {o.groups?.length ? <Typography sx={{ fontSize: 12, color: C.muted }}>{o.groups.join(", ")}</Typography> : null}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <InputBase
                  ref={params.InputProps.ref}
                  inputProps={{ ...params.inputProps, "aria-label": "Focus on a student" }}
                  placeholder="All my students — or pick one to focus on"
                  sx={{
                    mt: 1.25,
                    width: "100%",
                    px: 1.5,
                    py: 0.5,
                    borderRadius: "999px",
                    bgcolor: "rgba(255,255,255,.16)",
                    color: "#fff",
                    fontSize: 13.5,
                    "& input::placeholder": { color: "rgba(255,255,255,.85)", opacity: 1 },
                  }}
                  endAdornment={params.InputProps.endAdornment}
                />
              )}
              sx={{ position: "relative", "& .MuiAutocomplete-endAdornment .MuiSvgIcon-root": { color: "#fff" } }}
            />
          )}
        </Box>

        {/* Hand-over banner */}
        {humanMode && (
          <Box sx={{ px: 2, py: 1, bgcolor: "#FFF4D6", color: "#6B4A00", fontSize: 13, display: "flex", gap: 1, alignItems: "center", borderBottom: `1px solid #F5DC9A` }}>
            <SupportAgentIcon sx={{ fontSize: 18 }} />
            {conversation?.status === "assigned"
              ? "A member of our team is answering now."
              : "You're in the queue. A member of our team will reply here."}
          </Box>
        )}

        {/* Body */}
        <Box ref={scrollRef} sx={{ flex: 1, overflowY: "auto", px: 2, py: 2 }} aria-live="polite">
          {!enabled ? (
            <EmptyNote>The assistant is switched off at the moment. Use Messages to contact our team.</EmptyNote>
          ) : isParent && !childId && !loading ? (
            <EmptyNote>Add a student to your account to ask about their progress.</EmptyNote>
          ) : loading ? (
            <Box sx={{ display: "grid", placeItems: "center", mt: 8 }}>
              <CircularProgress size={28} sx={{ color: C.indigo }} />
            </Box>
          ) : !asked && greeting ? (
            /* Home view */
            <Box>
              <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-end", mb: 2 }}>
                <AssistantLogo size={56} />
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: C.ink, lineHeight: 1.2, pb: 0.5 }}>
                  {isParent
                    ? childName
                      ? `How is ${childName} getting on?`
                      : "How can I help?"
                    : isAdmin
                      ? "What do you need to know?"
                      : isTutor
                        ? focusStudent
                          ? `Let's look at ${focusStudent.full_name.split(" ")[0]}`
                          : "What would you like to look at?"
                        : "What shall we work on?"}
                </Typography>
              </Box>

              {activeReminders.slice(0, 2).map((r) => (
                <Box key={r.class_id} sx={{ mb: 1.25 }}>
                  <ClassCard r={r} onJoin={() => joinClass(r)} onView={viewClasses} />
                </Box>
              ))}

              <BotCard>{renderRich(greeting.body.replace(/^📅[^\n]*?today\.\s*/, ""), onInternalLink)}</BotCard>

              {!closed && (
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 2 }}>
                  {tiles.map(({ label, short, icon: Icon }) => (
                    <ButtonBase
                      key={label}
                      onClick={() => send(label)}
                      sx={{
                        p: 1.5,
                        minHeight: 84,
                        borderRadius: "14px",
                        bgcolor: "#fff",
                        border: `1px solid ${C.line}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        textAlign: "left",
                        gap: 1,
                        transition: "border-color .15s ease, background-color .15s ease",
                        "&:hover": { borderColor: C.indigo, bgcolor: C.indigoSoft },
                        "&:focus-visible": { outline: `2px solid ${C.indigo}`, outlineOffset: 2 },
                      }}
                    >
                      <Icon sx={{ color: C.indigo, fontSize: 22 }} />
                      <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>{short || label}</Typography>
                    </ButtonBase>
                  ))}
                </Box>
              )}

              {!closed && reports.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: C.muted, mb: 0.75 }}>Reports (PDF + CSV)</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    {reports.map((b) => (
                      <ButtonBase
                        key={b.label}
                        onClick={() => createReport(b)}
                        disabled={!!reporting}
                        sx={chipSx}
                      >
                        {reporting === b.type ? <CircularProgress size={14} sx={{ color: C.indigo }} /> : <ReportIcon sx={{ fontSize: 17, color: C.indigo }} />}
                        {b.label}
                      </ButtonBase>
                    ))}
                  </Box>
                </Box>
              )}

              {!closed && moreQuestions(role, staffKind).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <ButtonBase
                    onClick={() => setShowMore((v) => !v)}
                    aria-expanded={showMore}
                    sx={{ ...chipSx, color: C.indigo, fontWeight: 700, mb: showMore ? 1.25 : 0 }}
                  >
                    More questions
                    {showMore ? <ExpandLessIcon sx={{ fontSize: 18 }} /> : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                  </ButtonBase>
                  {showMore && moreQuestions(role, staffKind).map(({ group, items }) => (
                    <Box key={group} sx={{ mb: 1.25 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: C.muted, mb: 0.5 }}>{group}</Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {items
                          .map((it) => (typeof it === "string" ? { text: it } : it))
                          .map((it) => (
                            <ButtonBase key={it.text} onClick={() => askChip(it)} disabled={sending} sx={chipSx}>
                              {it.text}
                            </ButtonBase>
                          ))}
                      </Box>
                      {focusHint && !focusStudent && items.some((it) => it.needsFocus) && (
                        <Box sx={{ mt: 1 }}>
                          <BotCard>Pick a student in the focus box above first, then ask again.</BotCard>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            /* Conversation */
            messages.map((m, idx) => {
              if (m.sender_type === "system") {
                return (
                  <Typography key={m.id} sx={{ textAlign: "center", color: C.muted, fontSize: 12, my: 1.5 }}>
                    {m.body}
                  </Typography>
                );
              }
              if (m.sender_type === "visitor") {
                return (
                  <Box key={m.id} sx={{ display: "flex", justifyContent: "flex-end", my: 1.5 }}>
                    <Box sx={{ maxWidth: "82%", px: 1.75, py: 1, borderRadius: "18px 18px 6px 18px", bgcolor: C.indigo, color: "#fff", fontSize: 14.5, lineHeight: 1.45, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                      {m.body}
                    </Box>
                  </Box>
                );
              }
              const prevSame = messages[idx - 1]?.sender_type === m.sender_type;
              return (
                <Box key={m.id} sx={{ display: "flex", gap: 1, alignItems: "flex-start", my: 1.5 }}>
                  <Box sx={{ width: 28, flexShrink: 0, pt: 0.25 }}>
                    {!prevSame &&
                      (m.sender_type === "admin" ? (
                        <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#FFF4D6", color: "#8A5D00", display: "grid", placeItems: "center" }}>
                          <SupportAgentIcon sx={{ fontSize: 17 }} />
                        </Box>
                      ) : (
                        <AssistantLogo size={28} />
                      ))}
                  </Box>
                  <BotCard human={m.sender_type === "admin"}>
                    {m.sender_type === "admin" && <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#8A5D00", mb: 0.5 }}>Team member</Typography>}
                    {renderRich(m.body, onInternalLink)}
                    {(m.attachments || []).map((a) => (
                      <ReportCard key={a.report_id} attachment={a} base={base} />
                    ))}
                  </BotCard>
                </Box>
              );
            })
          )}

          {sending && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "center", my: 1.5 }}>
              <AssistantLogo size={28} thinking />
              <Box sx={{ px: 1.75, py: 1.25, borderRadius: "6px 18px 18px 18px", bgcolor: "#fff", border: `1px solid ${C.line}`, display: "flex", gap: 0.6 }} aria-label="Magic is thinking">
                {[0, 1, 2].map((i) => (
                  <Box key={i} sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: C.indigo, animation: `${dot} 1.2s ${i * 0.15}s infinite ease-in-out`, ...reduceMotion }} />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Composer */}
        <Box sx={{ px: 2, pt: 1, pb: 1.5, bgcolor: C.wash }}>
          {error && <Typography sx={{ color: C.red, fontSize: 12.5, mb: 0.75 }}>{error}</Typography>}

          {conversation?.awaiting_handoff && !humanMode && !closed && (
            <ButtonBase
              onClick={talkToTeam}
              disabled={sending}
              sx={{ width: "100%", mb: 1, py: 1, borderRadius: "12px", bgcolor: "#FFF4D6", color: "#6B4A00", fontWeight: 700, fontSize: 13.5, gap: 1, border: "1px solid #F5DC9A" }}
            >
              <SupportAgentIcon sx={{ fontSize: 18 }} /> Talk to our team
            </ButtonBase>
          )}

          {closed ? (
            <ButtonBase
              onClick={() => startConversation(childId)}
              sx={{ width: "100%", py: 1.25, borderRadius: "14px", bgcolor: C.indigo, color: "#fff", fontWeight: 700, fontSize: 14, gap: 1 }}
            >
              <NewChatIcon sx={{ fontSize: 18 }} /> Start a new chat
            </ButtonBase>
          ) : (
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              onClick={() => inputRef.current?.focus()}
              sx={{
                display: "flex",
                alignItems: "flex-end",
                gap: 1,
                p: 0.75,
                pl: 1.75,
                borderRadius: "18px",
                bgcolor: "#fff",
                border: `1.5px solid ${C.line}`,
                transition: "border-color .15s ease",
                "&:focus-within": { borderColor: C.indigo },
              }}
            >
              <InputBase
                inputRef={inputRef}
                fullWidth
                multiline
                maxRows={5}
                placeholder={
                  isParent
                    ? `Ask about ${childName || "your child"}'s progress, classes or homework`
                    : isAdmin
                      ? "Ask about a student, weak topics, missing content, figures or how to do something"
                      : isTutor
                        ? focusStudent
                          ? `Ask about ${focusStudent.full_name.split(" ")[0]}, or for a progress comment`
                          : "Ask about your groups, students, homework or classes"
                        : "Ask Magic anything about your learning"
                }
                value={input}
                disabled={!conversation || !enabled || sending}
                onChange={(e) => setInput(e.target.value.slice(0, 2000))}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                inputProps={{ "aria-label": "Message" }}
                sx={{ fontSize: 14.5, py: 0.75, color: C.ink }}
              />
              <IconButton
                type="submit"
                disabled={!input.trim() || !conversation || sending}
                aria-label="Send message"
                sx={{
                  width: 38,
                  height: 38,
                  bgcolor: C.indigo,
                  color: "#fff",
                  "&:hover": { bgcolor: "#2E2080" },
                  "&.Mui-disabled": { bgcolor: C.line, color: "#fff" },
                }}
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, gap: 1 }}>
            <Typography sx={{ fontSize: 11.5, color: C.muted }}>
              {remaining !== null && remaining <= 10
                ? `${remaining} message${remaining === 1 ? "" : "s"} left today`
                : copy.footer}
            </Typography>
            {conversation && !closed && !humanMode && !conversation.awaiting_handoff && !isAdmin && (
              <ButtonBase onClick={talkToTeam} disabled={sending} sx={{ fontSize: 12, fontWeight: 700, color: C.indigo, gap: 0.5, borderRadius: 1, px: 0.5 }}>
                <SupportAgentIcon sx={{ fontSize: 15 }} /> Talk to our team
              </ButtonBase>
            )}
          </Box>
        </Box>
      </Drawer>
    </>
  );
}

function ReportCard({ attachment, base }) {
  const [busy, setBusy] = useState("");
  const [failed, setFailed] = useState("");

  const download = async (format) => {
    setBusy(format);
    setFailed("");
    try {
      const res = await api.get(`${base}/reports/${attachment.report_id}/download`, { params: { format }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(attachment.title || "report").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      setFailed("Download failed — the report may have expired (reports are kept for 30 days). Ask for it again.");
    } finally {
      setBusy("");
    }
  };

  const btnSx = {
    px: 1.25,
    py: 0.6,
    gap: 0.6,
    borderRadius: "10px",
    fontSize: 12.5,
    fontWeight: 700,
    border: `1px solid ${C.line}`,
    bgcolor: "#fff",
    color: C.indigo,
    "&:hover": { bgcolor: C.indigoSoft, borderColor: C.indigo },
  };

  return (
    <Box sx={{ mt: 1.25, p: 1.25, borderRadius: "12px", bgcolor: C.wash, border: `1px solid ${C.line}` }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <ReportIcon sx={{ color: C.indigo, fontSize: 20 }} />
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: C.ink, lineHeight: 1.3 }}>{attachment.title}</Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {(attachment.formats || ["pdf", "csv"]).map((f) => (
          <ButtonBase key={f} onClick={() => download(f)} disabled={!!busy} sx={btnSx}>
            {busy === f ? <CircularProgress size={14} sx={{ color: C.indigo }} /> : f === "pdf" ? <PdfIcon sx={{ fontSize: 17 }} /> : <CsvIcon sx={{ fontSize: 17 }} />}
            Download {f.toUpperCase()}
          </ButtonBase>
        ))}
      </Box>
      {failed && <Typography sx={{ mt: 0.75, fontSize: 12, color: C.red }}>{failed}</Typography>}
    </Box>
  );
}

function BotCard({ children, human = false }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.75,
        py: 1.25,
        borderRadius: "6px 18px 18px 18px",
        bgcolor: human ? "#FFFBEF" : "#fff",
        border: `1px solid ${human ? "#F5DC9A" : C.line}`,
        color: C.ink,
        fontSize: 14.5,
        lineHeight: 1.55,
        wordBreak: "break-word",
        "& strong": { fontWeight: 700, color: C.ink },
      }}
    >
      {children}
    </Box>
  );
}

function EmptyNote({ children }) {
  return (
    <Box sx={{ display: "grid", justifyItems: "center", gap: 1.5, mt: 8, textAlign: "center", color: C.muted, fontSize: 14 }}>
      <AssistantLogo size={56} />
      <Box sx={{ maxWidth: 280 }}>{children}</Box>
    </Box>
  );
}
